import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import { Truck, Package, Shield, MapPin, FileCheck, Clock, CheckCircle } from 'lucide-react';
import { fetchServicesPage } from '../lib/sanity';
import { defaultServicesPage } from '../lib/defaultContent';
import type { ServicesPageContent } from '../lib/types';
import GrainBackground from '../components/GrainBackground';
import { useGrainAnimation } from '../hooks/useGrainAnimation';

const Services: React.FC = () => {
  const { grainAnimationEnabled } = useGrainAnimation();
  const [pageContent, setPageContent] = useState<ServicesPageContent | null>(null);
  const iconMap = { Truck, Package, Shield, MapPin, FileCheck, Clock, CheckCircle };

  useEffect(() => {
    fetchServicesPage().then((data) => {
      // Prioritize fetched data, fallback to defaults only if fetch fails
      setPageContent(data || defaultServicesPage);
    });
  }, []);

  // Don't render until we have content
  if (!pageContent) return null;

  return (
    <main className="relative pt-20">
      {grainAnimationEnabled && <GrainBackground />}
      <Section padding="default">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-primary-black mb-6">
            {pageContent.headerTitle}{' '}
            <span className="text-primary-red">{pageContent.headerHighlight}</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {pageContent.headerDescription}
          </p>
        </motion.div>

        {/* All service cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {pageContent.services.map((service, index) => {
            const IconComponent =
              iconMap[service.icon as keyof typeof iconMap] || Package;
            return (
              <Card key={index}>
                <div className="flex flex-col h-full">
                  <div className="w-16 h-16 rounded-full bg-primary-red/10 flex items-center justify-center mb-4">
                    <IconComponent className="w-8 h-8 text-primary-red" />
                  </div>
                  <h3 className="text-2xl font-playfair font-bold text-primary-black mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-700 mb-4 flex-grow">{service.description}</p>
                  <ul className="space-y-1">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary-sage" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            );
          })}
        </div>

        {/* How We Work — solid dark background */}
        <div className="mb-20">
          <div className="bg-primary-black rounded-2xl px-8 md:px-16 py-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <h2 className="text-4xl md:text-5xl font-playfair font-bold text-white mb-4">
                  {pageContent.howWeWorkTitle}
                </h2>
                <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto">
                  {pageContent.howWeWorkDescription}
                </p>
              </motion.div>

              <div className="grid md:grid-cols-4 gap-6">
                {pageContent.processSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="relative"
                  >
                    <div className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 h-full">
                      <div className="text-5xl font-playfair font-bold text-primary-red/60 mb-4">
                        {step.number}
                      </div>
                      <h3 className="text-xl font-playfair font-bold text-white mb-2">
                        {step.title}
                      </h3>
                      <p className="text-white/70 text-sm leading-relaxed">{step.description}</p>
                    </div>
                    {index < 3 && (
                      <div className="hidden md:block absolute top-1/3 -right-3 w-6 h-0.5 bg-primary-red/50" />
                    )}
                  </motion.div>
                ))}
              </div>
          </div>
        </div>

        {/* Delivery Coordination section */}
        <div id="logistics" className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-4">
              {pageContent.deliveryTitle}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl">
              {pageContent.deliveryDescription}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div className="space-y-6">
              <div className="bg-cream-50 rounded-xl p-6">
                <h3 className="font-playfair font-bold text-xl text-primary-black mb-4">
                  How We Deliver
                </h3>
                <ul className="space-y-3">
                  {pageContent.howWeDeliverPoints.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700">
                      <span className="w-6 h-6 rounded-full bg-primary-red text-white text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-cream-50 rounded-xl p-6">
                <h3 className="font-playfair font-bold text-xl text-primary-black mb-4">
                  For Contracted Clients
                </h3>
                <ul className="space-y-3">
                  {pageContent.contractedClientsPoints.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-secondary-sage flex-shrink-0 mt-2" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-xl overflow-hidden shadow-lg aspect-video"
            >
              <img
                src={pageContent.deliveryImage}
                alt="Delivery truck"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>

        {/* Ready to Discuss */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative rounded-2xl overflow-hidden p-8 md:p-12"
        >
          {/* Background video */}
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={pageContent.ctaVideo} type="video/mp4" />
          </video>
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-primary-black/70" />

          {/* Content */}
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-playfair font-bold text-white mb-4">
              {pageContent.ctaTitle}
            </h3>
            <p className="text-lg text-white/80 mb-8 max-w-2xl">
              {pageContent.ctaDescription}
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {pageContent.ctaPoints.map((item) => (
                <div key={item} className="flex items-center gap-2 text-white/90">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-red flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="primary" href="/contact">
                Contact Our Team
              </Button>
              <Button variant="primary" href="/track-record">
                View Our Track Record
              </Button>
            </div>
          </div>
        </motion.div>
      </Section>
    </main>
  );
};

export default Services;
